/* The three primitives the Marketplace artboard draws itself, rather than taking from the kit.
 *
 * `Alert`, `EmptyState` and `StatusBadge` each render a different shape from the reference — a
 * banner with a 3px spine and 13/16 padding, a 44px centred block, and a pill carrying a 6px dot
 * over an ink-on-tint pair. Substituting the shared ones changed the visual result on every tab,
 * which is what the rebuild exists to correct. Local, feature-scoped, and deliberately not exported
 * beyond this feature.
 */

import type { ReactElement } from 'react';

export function Banner({
  title,
  body,
}: {
  readonly title: string;
  readonly body: string;
}): ReactElement {
  return (
    <div className="lq-mp__banner">
      <div className="lq-mp__bannerTitle">{title}</div>
      <div className="lq-mp__bannerBody">{body}</div>
    </div>
  );
}

export function Empty({
  title,
  body,
}: {
  readonly title: string;
  readonly body: string;
}): ReactElement {
  return (
    <div className="lq-mp__empty">
      <div className="lq-mp__emptyTitle">{title}</div>
      <div className="lq-mp__emptyBody">{body}</div>
    </div>
  );
}

export type PillTone = 'ok' | 'warn' | 'error' | 'info' | 'unknown';

/** Ink on tint with a dot in the same ink. The dot inherits `currentcolor`, so a tone changes both
 * in one place and the two can never disagree. */
export function Pill({
  tone,
  label,
  small = false,
}: {
  readonly tone: PillTone;
  readonly label: string;
  readonly small?: boolean;
}): ReactElement {
  return (
    <span
      className={`lq-mp__pill lq-mp__pill--${tone}${small ? ' lq-mp__pill--sm' : ''}`}
    >
      <span className={small ? 'lq-mp__dot lq-mp__dot--sm' : 'lq-mp__dot'} />
      {label}
    </span>
  );
}
